// frontend/src/components/RouteMeta.jsx
// SEO (agency audit, pitch 17): every React page shares public/index.html's one <title> and description and has no
// canonical. This gives the public, non-/app pages their own title, description and canonical (plus og:/twitter:
// title, description and url). It only touches <head>, never the page itself.
// Pages not listed here are left exactly as before; leaving a listed page restores the defaults from index.html.
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const ORIGIN = 'https://nummius.com';

export const PAGE_META = {
  '/login': {
    title: 'Log in to Nummius',
    description: 'Log in to your Nummius account: charts, trading journal, backtesting and the Trading Arena.',
  },
  '/register': {
    title: 'Create your free Nummius account',
    description: 'Sign up for Nummius: the free Duelist plan includes the Trading Arena, practice and trade review. Upgrade any time.',
  },
  '/privacy': {
    title: 'Privacy Policy — Nummius',
    description: 'How Nummius collects, uses and protects your data.',
  },
  '/terms': {
    title: 'Terms of Service — Nummius',
    description: 'The terms that apply when you use Nummius, its charts, trading journal, backtesting and Trading Arena.',
  },
  '/practice': {
    title: 'Practice trading on historical charts — Nummius Arena',
    description: 'Practice trading on real historical charts: 1v1 duels, tournaments and daily challenges, or solo backtesting at your own pace.',
  },
  '/practice/tournaments': {
    title: 'Trading tournaments — Nummius Arena',
    description: 'Find official and creator-hosted trading tournaments on historical charts, see the standings, or host your own.',
  },
  '/duels/daily-challenge/play': {
    title: 'Daily trading challenge — Nummius Arena',
    description: 'One chart, one shot: today\'s daily challenge is the same setup for every trader. Make your trade, then review it.',
  },
};

function metaEl(attr, key) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    el.setAttribute('data-route-meta', '');
    document.head.appendChild(el);
  }
  return el;
}

const FIELDS = [
  ['name', 'description', 'description'],
  ['property', 'og:title', 'title'],
  ['property', 'og:description', 'description'],
  ['property', 'og:url', 'url'],
  ['name', 'twitter:title', 'title'],
  ['name', 'twitter:description', 'description'],
];

let defaults = null; // index.html's own values, read once before the first change

function readDefaults() {
  const d = { title: document.title, metas: {} };
  FIELDS.forEach(([attr, key]) => {
    const el = document.head.querySelector(`meta[${attr}="${key}"]`);
    d.metas[key] = el ? el.getAttribute('content') : null;
  });
  return d;
}

export function applyRouteMeta(pathname) {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  const page = PAGE_META[path];
  let canon = document.head.querySelector('link[rel="canonical"][data-route-meta]');
  if (page) {
    if (!defaults) defaults = readDefaults();
    const v = { ...page, url: ORIGIN + path };
    document.title = v.title;
    FIELDS.forEach(([attr, key, field]) => metaEl(attr, key).setAttribute('content', v[field]));
    if (!canon) {
      canon = document.createElement('link');
      canon.setAttribute('rel', 'canonical');
      canon.setAttribute('data-route-meta', '');
      document.head.appendChild(canon);
    }
    canon.setAttribute('href', v.url);
  } else if (defaults) {
    // Back to index.html's values, so a page we don't know about looks exactly as it did before.
    document.title = defaults.title;
    FIELDS.forEach(([attr, key]) => {
      const el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) return;
      if (defaults.metas[key] == null) el.remove();
      else el.setAttribute('content', defaults.metas[key]);
    });
    if (canon) canon.remove();
    defaults = null;
  }
}

export default function RouteMeta() {
  const { pathname } = useLocation();
  useEffect(() => { applyRouteMeta(pathname); }, [pathname]);
  return null;
}
